import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Briefcase, MapPin, DollarSign, FileText, CheckCircle2, Plus, X, Building2, UploadCloud, Loader2 } from 'lucide-react';
import { useCreateJobPosting, useJobCategories } from '@/hooks/useJob';
import { useCompanies } from '@/hooks/useCompany';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import AddressSelector from '@/components/common/AddressSelector';
import { imageApi } from '@/api/imageApi';
import { FileUploadDropzone } from '@/components/common';
import { Autocomplete, TextField } from '@mui/material';

const SUGGESTED_INDUSTRIES = [
  "Nhà hàng", "Quán cà phê", "Quán nhậu", "Trà sữa", "Tiệm bánh", "Bar/Pub", "Food Court", "Khách sạn/Resort", "Suất ăn công nghiệp"
];

const SUGGESTED_SKILLS = [
  "Quản lý nhà hàng", "Pha chế (Bartender)", "Pha chế cà phê (Barista)", "Phục vụ bàn", "Lên thực đơn (Menu planning)", "Nấu ăn (Bếp Á)", "Nấu ăn (Bếp Âu)", "Làm bánh (Bakery/Pastry)", "Chăm sóc khách hàng", "Quản lý chi phí (Food cost)", "Kiểm soát chất lượng", "Tuyển dụng & Đào tạo", "Tiếng Anh giao tiếp", "Marketing F&B", "Quản lý kho & Nhập hàng", "Kỹ năng bán hàng (Sales)", "Giải quyết tình huống"
];

const SUGGESTED_BENEFITS = [
  "Bảo hiểm Y tế / BHXH", "Lương tháng 13", "Thưởng doanh thu / Phí phục vụ (Service charge)", "Bao cơm ca", "Hỗ trợ gửi xe / xăng xe", "Đồng phục", "Khám sức khỏe định kỳ", "Du lịch / Đào tạo hàng năm", "Lộ trình thăng tiến rõ ràng", "Nghỉ phép năm (12 ngày+)", "Linh hoạt ca làm việc"
];

const JOB_TYPES = ['Full-time', 'Part-time', 'Freelance', 'Internship', 'Remote', 'Hybrid'].map((v) => ({ value: v, label: v }));
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];
const TIME_UNITS = [
  { value: 'month', label: 'Tháng' },
  { value: 'year', label: 'Năm' },
];

const schema = z.object({
  title: z.string().min(5, 'Tối thiểu 5 ký tự'),
  jobCategoryId: z.string().min(1, 'Nhập category ID'),
  jobCategoryName: z.string().min(1, 'Nhập category name'),
  jobType: z.string().nullish(),
  companyId: z.string().nullish(),
  companyName: z.string().nullish(),
  companySize: z.string().nullish(),
  companyType: z.string().nullish(),
  companyWebsite: z.string().nullish().or(z.literal('')),
  companyAddress: z.string().nullish(),
  streetAddress: z.string().nullish(),
  workingLocation: z.string().min(1, 'Vui lòng nhập địa điểm làm việc'),
  countryId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn quốc gia' }).min(1, 'Vui lòng chọn quốc gia'),
  provinceId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn tỉnh/thành phố' }).min(1, 'Vui lòng chọn tỉnh/thành phố'),
  districtId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn quận/huyện' }).min(1, 'Vui lòng chọn quận/huyện'),
  wardId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn phường/xã' }).min(1, 'Vui lòng chọn phường/xã'),
  salaryMin: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : 0;
    })
    .refine((val) => val > 0, { message: 'Lương min phải > 0' }),
  salaryMax: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : 0;
    })
    .refine((val) => val > 0, { message: 'Lương max phải > 0' }),
  currencyUnit: z.string().default('VND'),
  timeUnit: z.string().default('month'),
  vacancies: z
    .any()
    .transform((val) => {
      if (typeof val === 'number') return val;
      return val ? Number(String(val).replace(/\D/g, '')) : undefined;
    }),
  deadline: z.string().nullish(),
  probationMonths: z
    .any()
    .transform((val) => {
      if (typeof val === 'number' || val === undefined || val === null) return val;
      return val ? Number(String(val).replace(/\D/g, '')) : undefined;
    }),
  workingHours: z.string({ required_error: 'Vui lòng nhập giờ làm việc' }).min(1, 'Vui lòng nhập giờ làm việc'),
  description: z.string().min(10, 'Vui lòng nhập tối thiểu 10 ký tự'),
  educationRequirement: z.string().min(1, 'Vui lòng nhập trình độ học vấn'),
  experienceLevel: z.string().min(1, 'Vui lòng nhập kinh nghiệm yêu cầu'),
  skills: z.array(z.object({ value: z.string() })).nullish(),
  benefits: z.array(z.object({ value: z.string() })).nullish(),
  industries: z.array(z.object({ value: z.string() })).nullish(),
  areaUnit: z.string().default('m²'),
});
type FormData = z.infer<typeof schema>;

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

interface CreateJobPageProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function CreateJobPage({ onSuccess, onCancel }: CreateJobPageProps = {}) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();
  const { mutate: create, isPending } = useCreateJobPosting();
  const { data: companiesRes } = useCompanies({ createdBy: user?.id });



  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryId: 1,
      currencyUnit: 'VND',
      timeUnit: 'month',
      areaUnit: 'm²',
      skills: [],
      benefits: [],
      industries: [],
      vacancies: 1,
      jobType: 'Full-time',
      salaryMin: 0,
      salaryMax: 0,
      jobCategoryId: '',
      jobCategoryName: '',
      companyId: '',
      description: ''
    },
  });


  const { data: categories } = useJobCategories();

  const companies = companiesRes?.data || [];
  const companyOptions = companies.map(c => ({ value: c.id, label: c.name }));
  const categoryOptions = categories?.map(c => ({ value: c.id, label: c.name })) || [];

  useEffect(() => {
    if (companies.length > 0 && !watch('companyId')) {
      onCompanyChange(companies[0].id);
    }
  }, [companies]);

  const onCategoryChange = (id: string) => {
    const cat = categories?.find(c => c.id === id);
    if (!cat) return;
    setValue('jobCategoryId', cat.id, { shouldValidate: true });
    setValue('jobCategoryName', cat.name, { shouldValidate: true });
  };

  const onCompanyChange = (id: string) => {
    const comp = companies.find(c => c.id === id);
    if (!comp) return;

    setValue('companyId', comp.id, { shouldValidate: true });
    setValue('companyName', comp.name, { shouldValidate: true });
    setValue('companySize', comp.size, { shouldValidate: true });
    setValue('companyType', comp.businessTypeName, { shouldValidate: true });
    setValue('companyWebsite', comp.website || '', { shouldValidate: true });
    setValue('companyAddress', comp.address, { shouldValidate: true });

    if (comp.industry) {
      setValue('industries', [{ value: comp.industry }], { shouldValidate: true });
      const matchingCat = categories?.find(c =>
        c.name.toLowerCase() === comp.industry.toLowerCase() ||
        comp.industry.toLowerCase().includes(c.name.toLowerCase())
      );
      if (matchingCat) {
        setValue('jobCategoryId', matchingCat.id, { shouldValidate: true });
        setValue('jobCategoryName', matchingCat.name, { shouldValidate: true });
      }
    }
  };



  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= 1 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      alert('Một số file bị loại bỏ (không phải ảnh hoặc vượt quá 1MB).');
    }

    const newImgs: UploadedImg[] = validFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      url: URL.createObjectURL(file),
      isPrimary: false,
      file,
    }));

    setImages(prev => {
      const next = [...prev, ...newImgs];
      if (next.length > 0 && !next.some(i => i.isPrimary)) next[0].isPrimary = true;
      return next;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages(prev => {
      const next = prev.filter(img => img.id !== id);
      if (next.length > 0 && !next.some(img => img.isPrimary)) {
        next[0].isPrimary = true;
      }
      return next;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages(prev => prev.map(img => ({ ...img, isPrimary: img.id === id })));
  };

  const onSubmit = async (data: FormData) => {
    if (isSubmitting || isPending) return;
    setIsSubmitting(true);
    try {
      setIsUploading(true);

      const finalImages = [];
      const imageIds = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res?.data?.id) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
            imageIds.push(res.data.id);
          }
        } else if (img.id) {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
          imageIds.push(img.id);
        }
      }

      const payload = {
        ...data,
        skills: data.skills?.map((s) => s.value) ?? [],
        benefits: data.benefits?.map((b) => b.value) ?? [],
        industries: data.industries?.map((i) => i.value) ?? [],
        companyWebsite: data.companyWebsite || undefined,
        deadline: data.deadline ? new Date(data.deadline).toISOString() : undefined,
        createdBy: user?.id,
        imageIds,
        images: finalImages,
      };

      create(payload as any, {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          } else {
            navigate('/viec-lam/quan-ly?tab=postings');
          }
        },
        onError: () => {
          setIsSubmitting(false);
        }
      });
    } catch (err) {
      console.error(err);
      addToast('error', 'Có lỗi xảy ra khi tải ảnh hoặc lưu tin đăng.');
      setIsSubmitting(false);
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log("Validation Errors: ", errors);
    addToast('error', 'Form chưa hợp lệ. Vui lòng kiểm tra lại các trường báo đỏ!');
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in pb-10">
      <div className="flex items-center gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl" style={{ color: 'var(--text-1)' }}>
            Đăng tin tuyển dụng
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-3)' }}>
            Tìm kiếm ứng viên phù hợp cho vị trí của bạn
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        <FormCard title="Thông tin vị trí" icon={<Briefcase size={16} />}>
          <Input label="Tên vị trí *" placeholder="VD: Head Chef, Store Manager" error={errors.title?.message} {...register('title')} />
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="jobCategoryId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Ngành nghề *"
                  options={categoryOptions}
                  placeholder="Chọn ngành nghề..."
                  error={errors.jobCategoryId?.message}
                  value={field.value}
                  onChange={(e) => {
                    const val = e.target.value;
                    field.onChange(val);
                    onCategoryChange(val);
                  }}
                />
              )}
            />
            <Select label="Loại công việc *" options={JOB_TYPES} placeholder="Chọn loại" {...register('jobType')} />
          </div>
        </FormCard>

        <FormCard title="Thông tin công ty" icon={<Building2 size={16} />}>
          <Controller
            name="companyId"
            control={control}
            render={({ field }) => (
              <Select
                label="Chọn công ty "
                options={companyOptions}
                placeholder="Tìm công ty..."
                error={errors.companyId?.message}
                value={field.value}
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val);
                  onCompanyChange(val);
                }}
              />
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Quy mô" placeholder="Tự động điền..." value={watch('companySize') || ''} readOnly disabled />
            <Input label="Website" placeholder="Tự động điền..." value={watch('companyWebsite') || ''} readOnly disabled />
          </div>
          <Input label="Địa chỉ công ty" placeholder="Tự động điền..." value={watch('companyAddress') || ''} readOnly disabled />
        </FormCard>

        <FormCard title="Địa điểm làm việc" icon={<MapPin size={16} />}>
          <Input label="Địa chỉ cụ thể" placeholder="123 Nguyễn Huệ, Quận 1" {...register('streetAddress')} />
          <Input label="Địa điểm làm việc *" placeholder="Remote / Hà Nội / TP.HCM" error={errors.workingLocation?.message} {...register('workingLocation')} />

          <div className="pt-4 border-t border-gray-100">
            <AddressSelector
              countryId={watch('countryId')}
              provinceId={watch('provinceId') ?? undefined}
              districtId={watch('districtId') ?? undefined}
              wardId={watch('wardId') ?? undefined}
              onCountryChange={(id) => setValue('countryId', id, { shouldValidate: true })}
              onProvinceChange={(id) => setValue('provinceId', id, { shouldValidate: true })}
              onDistrictChange={(id) => setValue('districtId', id, { shouldValidate: true })}
              onWardChange={(id) => setValue('wardId', id, { shouldValidate: true })}
              errors={{
                countryId: errors.countryId?.message,
                provinceId: errors.provinceId?.message,
                districtId: errors.districtId?.message,
                wardId: errors.wardId?.message,
              }}
              required
            />
          </div>
        </FormCard>

        <FormCard title="Lương & Lịch làm việc" icon={<DollarSign size={16} />}>
          <div className="grid grid-cols-3 gap-4">
            <NumberInput label="Lương tối thiểu *" placeholder="1000" error={errors.salaryMin?.message} value={watch('salaryMin')} {...register('salaryMin')} />
            <NumberInput label="Lương tối đa *" placeholder="2000" error={errors.salaryMax?.message} value={watch('salaryMax')} {...register('salaryMax')} />
            <Select label="Đơn vị tiền" options={CURRENCY} {...register('currencyUnit')} />
          </div>
          <Select label="Tính theo" options={TIME_UNITS} {...register('timeUnit')} />
          <div className="grid grid-cols-2 gap-4">
            <NumberInput label="Số lượng tuyển" placeholder="3" value={watch('vacancies')} {...register('vacancies')} />
            <NumberInput label="Thử việc (tháng)" placeholder="2" value={watch('probationMonths')} {...register('probationMonths')} />
            <Input label="Giờ làm việc *" placeholder="9AM - 6PM" error={errors.workingHours?.message} {...register('workingHours')} />
            <Input label="Hạn nộp hồ sơ" type="date" {...register('deadline')} />
          </div>
        </FormCard>

        <FormCard title="Mô tả công việc" icon={<FileText size={16} />}>
          <Textarea
            label="Mô tả chi tiết *"
            rows={6}
            placeholder="Mô tả công việc, nhiệm vụ hàng ngày, môi trường làm việc..."
            error={errors.description?.message}
            {...register('description')}
          />
          <Input label="Trình độ học vấn *" placeholder="Cử nhân / Kỹ sư..." error={errors.educationRequirement?.message} {...register('educationRequirement')} />
          <Input label="Kinh nghiệm yêu cầu *" placeholder="1 năm / 2 năm..." error={errors.experienceLevel?.message} {...register('experienceLevel')} />
        </FormCard>

        <FormCard title="Ngành nghề" icon={<Briefcase size={16} />}>
          <Controller
            name="industries"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                freeSolo
                options={SUGGESTED_INDUSTRIES}
                value={field.value?.map((s) => s.value) || []}
                onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="VD: Nhà hàng, Quán cà phê (Enter để thêm)"
                    error={!!errors.industries}
                    helperText={errors.industries?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#f97316' },
                        '&.Mui-focused fieldset': { borderColor: '#f97316' },
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </FormCard>

        <FormCard title="Kỹ năng yêu cầu" icon={<CheckCircle2 size={16} />}>
          <Controller
            name="skills"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                freeSolo
                options={SUGGESTED_SKILLS}
                value={field.value?.map((s) => s.value) || []}
                onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="VD: Quản lý nhà hàng, Pha chế... (Enter để thêm)"
                    error={!!errors.skills}
                    helperText={errors.skills?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#f97316' },
                        '&.Mui-focused fieldset': { borderColor: '#f97316' },
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </FormCard>

        <FormCard title="Phúc lợi" icon={<CheckCircle2 size={16} />}>
          <Controller
            name="benefits"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                freeSolo
                options={SUGGESTED_BENEFITS}
                value={field.value?.map((s) => s.value) || []}
                onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="VD: Bao cơm ca, Thưởng tháng 13... (Enter để thêm)"
                    error={!!errors.benefits}
                    helperText={errors.benefits?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#f97316' },
                        '&.Mui-focused fieldset': { borderColor: '#f97316' },
                      }
                    }}
                  />
                )}
              />
            )}
          />
        </FormCard>

        <FormCard title="Hình ảnh & Môi trường làm việc" icon={<UploadCloud size={16} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn ảnh môi trường làm việc, văn phòng..."
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={`relative aspect-video rounded-xl overflow-hidden border-2 group ${img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"}`}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormCard>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8">
          <Button type="button" variant="outline" onClick={onCancel || (() => navigate('/viec-lam'))}>
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading || isSubmitting} disabled={isPending || isUploading || isSubmitting}>
            Đăng tin
          </Button>
        </div>
      </form>
    </div>
  );
}

function FormCard({ title, icon, children, className }: { title: string; icon: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-2xl border p-6 space-y-4', className)} style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-base flex items-center gap-2 pb-3 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <span style={{ color: 'var(--brand)' }}>{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}


